/**
 * Agent Center · Gestión y configuración de agentes IA.
 * Todos en estado "planned" inicialmente. Permite cambiar status, modo, schedule.
 * NO ejecuta agentes (eso es Fase 2).
 */
import { useEffect, useState } from "react";
import { api } from "../../lib/api";
import { toast } from "sonner";
import {
  Robot, Lightning, Clock, Pause, CheckCircle, ChartLineUp, MagicWand,
  ArrowsClockwise, CaretDown, CaretRight, Sparkle,
} from "@phosphor-icons/react";

const STATUS_OPTIONS = [
  { value: "planned",  label: "Planned",  icon: MagicWand,   cls: "text-zinc-400 border-zinc-500/40 bg-zinc-500/10" },
  { value: "active",   label: "Active",   icon: Lightning,   cls: "text-emerald-300 border-emerald-500/40 bg-emerald-500/10" },
  { value: "paused",   label: "Paused",   icon: Pause,       cls: "text-amber-300 border-amber-500/40 bg-amber-500/10" },
  { value: "retired",  label: "Retired",  icon: CheckCircle, cls: "text-[var(--ag-text-muted)] border-[var(--ag-border)] bg-[var(--ag-midnight)]/40" },
];

const MODE_LABELS = {
  advisory: "Advisory (sugiere, no actúa)",
  scheduled: "Scheduled (cron periódico)",
  "on-demand": "On-demand (manual)",
  interactive: "Interactive (chat)",
  always: "Always (siempre activo)",
};

export default function AgentCenter() {
  const [agents, setAgents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [byStatus, setByStatus] = useState({});
  const [expanded, setExpanded] = useState({});

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/founder-os/agents");
      setAgents(data.items || []);
      setByStatus(data.by_status || {});
    } catch (e) { console.error(e); }
    finally { setLoading(false); }
  };

  useEffect(() => { load(); }, []);

  const updateStatus = async (agent, newStatus) => {
    try {
      await api.patch(`/founder-os/agents/${agent.id}`, { status: newStatus });
      toast.success(`${agent.code} → ${newStatus}`);
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Error");
    }
  };

  return (
    <div className="p-6 lg:p-8 space-y-5" data-testid="founder-agents">
      <header className="flex flex-col sm:flex-row sm:items-end sm:justify-between gap-3">
        <div>
          <div className="text-[10px] uppercase tracking-[0.22em] text-[var(--ag-gold)] font-semibold flex items-center gap-1.5">
            <Robot size={11} weight="fill" /> Agent Center
          </div>
          <h1 className="font-display text-3xl md:text-4xl font-bold tracking-tighter mt-2">
            Agentes IA · gestión y configuración
          </h1>
          <p className="text-xs text-[var(--ag-text-dim)] mt-1">
            {agents.length} agentes registrados · {byStatus.active || 0} activos · {byStatus.planned || 0} planned
            {agents.length === 0 && " · sin seedear"}
          </p>
        </div>
        <div className="flex items-center gap-2">
          {agents.length === 0 && !loading && (
            <button
              type="button"
              onClick={async () => {
                try {
                  const { data } = await api.post("/founder-os/seed-now");
                  toast.success(`Seed OK · ${data.agents_total} agentes · ${data.audits_total} audits · ${data.security_total} security`);
                  await load();
                } catch (e) {
                  toast.error(e?.response?.data?.detail || e.message || "Error en seed");
                }
              }}
              className="inline-flex items-center gap-1.5 text-xs px-3 py-2 rounded bg-[var(--ag-gold)] text-[var(--ag-midnight)] font-semibold hover:opacity-90"
              data-testid="agents-seed-now"
            >
              <Sparkle size={13} weight="fill" /> Sembrar agentes ahora
            </button>
          )}
          <button
            type="button"
            onClick={load}
            className="inline-flex items-center gap-1.5 text-xs text-[var(--ag-text-dim)] hover:text-[var(--ag-gold)] px-3 py-2 rounded border border-[var(--ag-border)] hover:border-[var(--ag-gold)]/50"
            data-testid="agents-refresh"
          >
            <ArrowsClockwise size={13} /> Refrescar
          </button>
        </div>
      </header>

      {/* Stats por status */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-2">
        {STATUS_OPTIONS.map((s) => (
          <div key={s.value} className={`px-3 py-2 rounded border ${s.cls}`}>
            <div className="text-[9px] uppercase tracking-wider opacity-70 flex items-center gap-1">
              <s.icon size={10} weight="fill" /> {s.label}
            </div>
            <div className="font-bold tabular-nums text-lg">{byStatus[s.value] || 0}</div>
          </div>
        ))}
      </div>

      {/* Agentes */}
      {loading ? (
        <div className="text-xs text-[var(--ag-text-dim)]">Cargando…</div>
      ) : (
        <div className="space-y-3" data-testid="agents-list">
          {agents.map((a) => {
            const stm = STATUS_OPTIONS.find(s => s.value === a.status) || STATUS_OPTIONS[0];
            const isExp = !!expanded[a.id];
            return (
              <div
                key={a.id}
                className="rounded-lg border border-[var(--ag-border)] bg-[var(--ag-surface)] overflow-hidden"
                data-testid={`agent-${a.code}`}
              >
                <button
                  type="button"
                  onClick={() => setExpanded({ ...expanded, [a.id]: !isExp })}
                  className="w-full text-left p-4 hover:bg-[var(--ag-midnight)]/20 transition-colors"
                >
                  <div className="flex items-start gap-3">
                    <div className="w-10 h-10 rounded-lg bg-[var(--ag-gold)]/10 grid place-items-center text-[var(--ag-gold)] shrink-0">
                      <Robot size={20} weight="duotone" />
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 flex-wrap">
                        <span className="font-mono text-[10px] text-[var(--ag-text-muted)] font-bold">{a.code}</span>
                        <span className="font-display text-base font-bold tracking-tight">{a.name}</span>
                        <span className={`text-[9px] uppercase tracking-wider px-1.5 py-0.5 rounded border inline-flex items-center gap-1 ${stm.cls}`}>
                          <stm.icon size={9} weight="fill" /> {stm.label}
                        </span>
                      </div>
                      <p className="text-xs text-[var(--ag-text-dim)] mt-1.5 line-clamp-2">{a.description}</p>
                      <div className="flex flex-wrap gap-2 mt-2 text-[10px] text-[var(--ag-text-muted)]">
                        {a.mode && <span>Modo: <strong>{MODE_LABELS[a.mode] || a.mode}</strong></span>}
                        {a.schedule && <span>Schedule: <strong>{a.schedule}</strong></span>}
                        {a.model && <span>Modelo: <strong>{a.model}</strong></span>}
                      </div>
                    </div>
                    {isExp
                      ? <CaretDown size={16} className="text-[var(--ag-text-dim)] mt-2 shrink-0" />
                      : <CaretRight size={16} className="text-[var(--ag-text-dim)] mt-2 shrink-0" />}
                  </div>
                </button>
                {isExp && (
                  <div className="px-4 pb-4 pt-2 border-t border-[var(--ag-border)] space-y-3">
                    {a.future_capabilities && a.future_capabilities.length > 0 && (
                      <div>
                        <div className="text-[10px] uppercase tracking-wider text-[var(--ag-text-muted)] font-semibold mb-2">
                          Capacidades futuras (Fase 2)
                        </div>
                        <ul className="space-y-1">
                          {a.future_capabilities.map((cap, i) => (
                            <li key={i} className="text-xs text-[var(--ag-text-dim)] flex items-start gap-2">
                              <Sparkle size={10} className="text-[var(--ag-gold)] mt-0.5 shrink-0" />
                              {cap}
                            </li>
                          ))}
                        </ul>
                      </div>
                    )}
                    <div className="flex items-center gap-2 pt-2 border-t border-[var(--ag-border)]">
                      <span className="text-[10px] uppercase tracking-wider text-[var(--ag-text-muted)] font-semibold">Cambiar status:</span>
                      {STATUS_OPTIONS.map((s) => (
                        <button
                          key={s.value}
                          type="button"
                          onClick={() => updateStatus(a, s.value)}
                          disabled={a.status === s.value}
                          className={`text-[10px] uppercase tracking-wider px-2 py-1 rounded border transition-colors ${
                            a.status === s.value
                              ? `${s.cls} opacity-60 cursor-default`
                              : "border-[var(--ag-border)] text-[var(--ag-text-dim)] hover:border-[var(--ag-gold)]/50 hover:text-[var(--ag-gold)]"
                          }`}
                          data-testid={`agent-${a.code}-set-${s.value}`}
                        >
                          {s.label}
                        </button>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}

      <p className="text-[10px] text-[var(--ag-text-muted)] text-center pt-4">
        Esta fase construye la <strong>infraestructura, gestión y gobierno</strong> de los agentes.
        La ejecución autónoma con tool calling, RAG y memoria se construye en la <strong>Fase 2 · AI Agents</strong>.
      </p>
    </div>
  );
}
